/**
 * Ted #17 (#921) — business-chosen service colors.
 *
 * Ted, 09-09: *"I'd like each service/category to have its own color, so
 * when looking at the daily calendar we can immediately identify what each
 * dog is there for … an assessment could be black, grooming lime green …
 * Gingr lets us choose from about 10 different colors … once assigned, that
 * service color stays consistent throughout the calendar and daily
 * dashboard."*
 *
 * The owner picks a KEY from this fixed palette (stored on
 * `service_types.color`, validated at the Zod boundary); the hex values live
 * here, once, and the admin app's `service-colors.css` declares the same
 * values as tokens (a guard test keeps the two in step). Never a free hex:
 * the palette is what keeps a tenant's colors legible and consistent.
 *
 * Every entry carries three colors, each with a job:
 *   · `rule` — the 4 px left edge on a row / the chip border. Non-text, so
 *     WCAG 1.4.11's 3:1 against the white surface is the bar.
 *   · `bg`   — the tint behind a chip or a service name.
 *   · `fg`   — the text ON that tint. WCAG AA text is 4.5:1 against `bg`.
 * `service-colors.test.ts` computes both ratios over this table — the
 * contrast promise is a test, not a comment.
 *
 * Service colors are a THIRD token family (DESIGN-PHILOSOPHY Principle 6):
 * structural `--ferro-*` still carries STATUS (checked in / unconfirmed /
 * stale) and the tenant's `--brand-*` pair still carries identity. A
 * service's color says WHAT the dog is here for and nothing else; status
 * keeps its own tone and its word beside it. A service with no color falls
 * back to neutral — never to another service's color.
 */

export const SERVICE_COLOR_KEYS = [
  'black',
  'lime',
  'sky',
  'coral',
  'amber',
  'violet',
  'teal',
  'rose',
  'slate',
  'forest',
] as const;

export type ServiceColorKey = (typeof SERVICE_COLOR_KEYS)[number];

export interface ServiceColorSwatch {
  key: ServiceColorKey;
  /** The word the owner sees on the picker. */
  label: string;
  /** Left rule / chip border (non-text; ≥ 3:1 on white). */
  rule: string;
  /** The tint behind a chip or a tinted service name. */
  bg: string;
  /** Text on the tint (≥ 4.5:1 on `bg`). */
  fg: string;
}

export const SERVICE_COLORS: Readonly<Record<ServiceColorKey, ServiceColorSwatch>> = {
  black: { key: 'black', label: 'Black', rule: '#1f1f1f', bg: '#ececec', fg: '#1a1a1a' },
  lime: { key: 'lime', label: 'Lime green', rule: '#5f9400', bg: '#edf7d6', fg: '#3a5a00' },
  sky: { key: 'sky', label: 'Sky blue', rule: '#2f8fd0', bg: '#e2f0fb', fg: '#0f4c75' },
  coral: { key: 'coral', label: 'Coral', rule: '#d9503d', bg: '#fde5e1', fg: '#8a2a1d' },
  amber: { key: 'amber', label: 'Amber', rule: '#b98200', bg: '#fdf0c8', fg: '#6a4a00' },
  violet: { key: 'violet', label: 'Violet', rule: '#7c4ee6', bg: '#ece6fd', fg: '#472a9a' },
  teal: { key: 'teal', label: 'Teal', rule: '#0e8a7f', bg: '#daf4f1', fg: '#0a5751' },
  rose: { key: 'rose', label: 'Rose', rule: '#d63d93', bg: '#fbe3f0', fg: '#88205b' },
  slate: { key: 'slate', label: 'Slate', rule: '#64748b', bg: '#e8ecf1', fg: '#334155' },
  forest: { key: 'forest', label: 'Forest green', rule: '#2f7a3b', bg: '#dfefe1', fg: '#1e4a26' },
};

export function isServiceColorKey(raw: unknown): raw is ServiceColorKey {
  return typeof raw === 'string' && (SERVICE_COLOR_KEYS as ReadonlyArray<string>).includes(raw);
}

/**
 * The CSS class a consumer puts on a row / chip / cell so the token layer
 * (`service-colors.css`) resolves `--service-rule` / `--service-bg` /
 * `--service-fg` for it. Null / unknown → no class: the consumer's neutral
 * fallbacks apply, and no service ever inherits another's color.
 */
export function serviceColorClass(key: string | null | undefined): string {
  return isServiceColorKey(key) ? `ferro-service--${key}` : '';
}

// ── Contrast (WCAG 2.x relative luminance) — used by the test, exported so
// a picker could also refuse an off-palette value if one ever appears.

function channel(c: number): number {
  const v = c / 255;
  return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
}

export function relativeLuminance(hex: string): number {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}
